/**
 * Funnel Configuration
 *
 * Defines the conversion funnel stages and defaults.
 * Each product can override any property — unspecified fields
 * inherit from defaultFunnel (the "super").
 */

export type FunnelStage = 'discovery' | 'gate' | 'engaged' | 'paywall'

export type FunnelConfig = {
  /** Tokens granted to anonymous sessions */
  freeTokens: number
  /** Bonus tokens on signup */
  signupBonus: number
  /** After N free actions, require signup (0 = no gate) */
  actionsBeforeGate: number
  /** What the token unit is called */
  unit: {singular: string; plural: string}
  /** CTA copy */
  cta: {
    purchase: string
    signup: string
    gateTitle: string
    gateMessage: string
  }
  /** Error messages */
  messages: {
    insufficientTokens: string
  }
  /** HTTP status code for insufficient tokens */
  insufficientTokensStatus: number
}

export const defaultFunnel: FunnelConfig = {
  freeTokens: 3,
  signupBonus: 1,
  actionsBeforeGate: 3,
  unit: {singular: 'token', plural: 'tokens'},
  cta: {
    purchase: 'Buy tokens',
    signup: 'Sign up to continue',
    gateTitle: 'Create your account',
    gateMessage: 'Sign up to keep creating. Your work is saved.',
  },
  messages: {
    insufficientTokens: 'No tokens remaining. Purchase more to continue.',
  },
  insufficientTokensStatus: 402,
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}

export type FunnelOverrides = DeepPartial<FunnelConfig>

function deepMerge<T extends Record<string, unknown>>(base: T, overrides: Record<string, unknown>): T {
  const result = {...base}
  for (const key of Object.keys(overrides)) {
    const val = overrides[key]
    if (val !== undefined && val !== null && typeof val === 'object' && !Array.isArray(val)) {
      result[key as keyof T] = deepMerge(
        (base[key] ?? {}) as Record<string, unknown>,
        val as Record<string, unknown>,
      ) as T[keyof T]
    } else if (val !== undefined) {
      result[key as keyof T] = val as T[keyof T]
    }
  }
  return result
}

/** Resolve product funnel overrides onto defaults */
export function resolveFunnel(overrides?: FunnelOverrides): FunnelConfig {
  if (!overrides) return {...defaultFunnel, cta: {...defaultFunnel.cta}, unit: {...defaultFunnel.unit}, messages: {...defaultFunnel.messages}}
  return deepMerge(defaultFunnel, overrides as Record<string, unknown>)
}
